import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const API_URL = import.meta.env.VITE_API_URL as string;

// ─── Types ────────────────────────────────────────────────────────────────────

interface AtsCategory {
  score: number;
  weight: number;
  issues: string[];
  suggestions: string[];
}

interface ScanResult {
  leadId: string;
  resumeS3Key: string;
  overall: number;
  parseability: AtsCategory;
  contentCompleteness: AtsCategory;
  keywordRelevance: AtsCategory;
  formattingStructure: AtsCategory;
  writingQuality: AtsCategory;
}

type Step = 'upload' | 'scanning' | 'email-gate' | 'capturing' | 'score-reveal';

// ─── Helpers ──────────────────────────────────────────────────────────────────

async function guestPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error((data as { message?: string }).message ?? 'Request failed');
  }
  return res.json() as Promise<T>;
}

function scoreColor(score: number): string {
  if (score >= 75) return 'text-emerald-600';
  if (score >= 55) return 'text-amber-500';
  return 'text-red-500';
}

function scoreRingColor(score: number): string {
  if (score >= 75) return '#059669';
  if (score >= 55) return '#d97706';
  return '#dc2626';
}

function scoreLabel(score: number): string {
  if (score >= 75) return 'Strong';
  if (score >= 55) return 'Good';
  if (score >= 35) return 'Fair';
  return 'Needs Work';
}

const CATEGORY_LABELS: Record<keyof Omit<ScanResult, 'leadId' | 'resumeS3Key' | 'overall'>, string> = {
  parseability: 'Parseability',
  contentCompleteness: 'Content Completeness',
  keywordRelevance: 'Keyword Relevance',
  formattingStructure: 'Formatting & Structure',
  writingQuality: 'Writing Quality',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function ScoreRing({ score }: { score: number }) {
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * circumference;
  const color = scoreRingColor(score);

  return (
    <div className="relative w-36 h-36 flex items-center justify-center">
      <svg className="absolute inset-0 -rotate-90" width="144" height="144" viewBox="0 0 144 144">
        <circle cx="72" cy="72" r={radius} fill="none" stroke="#e5e7eb" strokeWidth="10" />
        <circle
          cx="72" cy="72" r={radius} fill="none"
          stroke={color} strokeWidth="10"
          strokeDasharray={`${progress} ${circumference}`}
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 0.8s ease' }}
        />
      </svg>
      <div className="flex flex-col items-center">
        <span className={`text-4xl font-bold leading-none ${scoreColor(score)}`}>{score}</span>
        <span className="text-xs text-gray-400 mt-1">/ 100</span>
      </div>
    </div>
  );
}

function CategoryBar({ label, score, blurred }: { label: string; score: number; blurred: boolean }) {
  return (
    <div className={blurred ? 'blur-sm select-none pointer-events-none' : ''}>
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm text-gray-600">{label}</span>
        <span className="text-sm font-medium text-gray-900">{score}</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-2 rounded-full transition-all duration-700"
          style={{
            width: `${score}%`,
            backgroundColor: scoreRingColor(score),
          }}
        />
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function GuestScan() {
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── File selection ──────────────────────────────────────────────────────────

  function handleFileSelect(selected: File) {
    setError(null);
    if (!selected.name.toLowerCase().endsWith('.pdf')) {
      setError('Only PDF files are supported. Please upload a PDF version of your resume.');
      return;
    }
    if (selected.size > 5 * 1024 * 1024) {
      setError('File exceeds the 5 MB limit. Please reduce the file size and try again.');
      return;
    }
    setFile(selected);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFileSelect(dropped);
  }

  // ── Scan ────────────────────────────────────────────────────────────────────

  async function handleScan() {
    if (!file) return;
    setError(null);
    setStep('scanning');

    const formData = new FormData();
    formData.append('resume', file);

    try {
      const res = await fetch(`${API_URL}/ats/scan/guest`, {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({ message: res.statusText }));
        throw new Error((data as { message?: string }).message ?? 'Scan failed');
      }

      const result = (await res.json()) as ScanResult;
      setScanResult(result);
      setStep('email-gate');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Scan failed. Please try again.');
      setStep('upload');
    }
  }

  // ── Email capture ───────────────────────────────────────────────────────────

  async function handleCapture(e: React.FormEvent) {
    e.preventDefault();
    if (!scanResult) return;
    setError(null);
    setStep('capturing');

    try {
      await guestPost('/leads/capture', {
        email: email.trim().toLowerCase(),
        leadId: scanResult.leadId,
        atsScore: scanResult.overall,
        resumeS3Key: scanResult.resumeS3Key,
      });
      setStep('score-reveal');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not submit email. Please try again.');
      setStep('email-gate');
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Nav */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <Link to="/" className="text-lg font-bold text-gray-900 tracking-tight">
          Slingshotio
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900">
            Sign in
          </Link>
          <Link
            to="/signup"
            className="text-sm font-medium px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Create account
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-start justify-center px-4 py-12">
        <div className="w-full max-w-lg">

          {/* ── Upload step ──────────────────────────────────────────────── */}
          {(step === 'upload' || step === 'scanning') && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Check your resume score</h1>
              <p className="text-gray-500 text-sm mb-7">
                Find out how your resume performs against ATS systems in seconds.
              </p>

              {/* Drop zone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={[
                  'border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors',
                  dragOver
                    ? 'border-indigo-400 bg-indigo-50'
                    : file
                    ? 'border-emerald-400 bg-emerald-50'
                    : 'border-gray-200 bg-gray-50 hover:border-indigo-300 hover:bg-indigo-50/40',
                ].join(' ')}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFileSelect(f);
                  }}
                />

                {file ? (
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]">{file.name}</p>
                    <p className="text-xs text-gray-400">{(file.size / 1024).toFixed(0)} KB · Click to change</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                      <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                          d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <p className="text-sm font-medium text-gray-700">Drop your resume here</p>
                    <p className="text-xs text-gray-400">PDF · max 5 MB</p>
                  </div>
                )}
              </div>

              {error && (
                <p className="mt-3 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
              )}

              <button
                onClick={handleScan}
                disabled={!file || step === 'scanning'}
                className="mt-5 w-full py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {step === 'scanning' ? (
                  <>
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Analyzing…
                  </>
                ) : (
                  'Scan My Resume'
                )}
              </button>

              <p className="mt-4 text-xs text-center text-gray-400">
                Free, no account needed · Your file is not stored
              </p>
            </div>
          )}

          {/* ── Email gate ───────────────────────────────────────────────── */}
          {(step === 'email-gate' || step === 'capturing') && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8">
              <div className="flex flex-col items-center text-center mb-6">
                <div className="w-14 h-14 rounded-full bg-indigo-50 flex items-center justify-center mb-4">
                  <svg className="w-7 h-7 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-1">Your score is ready</h2>
                <p className="text-sm text-gray-500">Enter your email to see your ATS score and get a copy in your inbox.</p>
              </div>

              <form onSubmit={handleCapture} className="space-y-3">
                <input
                  type="email"
                  required
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />

                {error && (
                  <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={step === 'capturing'}
                  className="w-full py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {step === 'capturing' ? (
                    <>
                      <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Just a moment…
                    </>
                  ) : (
                    'Show My Score'
                  )}
                </button>
              </form>

              <p className="mt-4 text-xs text-center text-gray-400">
                No spam. Unsubscribe anytime. We'll send you one email with your score.
              </p>
            </div>
          )}

          {/* ── Score reveal ─────────────────────────────────────────────── */}
          {step === 'score-reveal' && scanResult && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8">
              <div className="flex flex-col items-center mb-8">
                <p className="text-sm font-medium text-gray-500 mb-4">Your ATS Score</p>
                <ScoreRing score={scanResult.overall} />
                <p className={`mt-3 text-lg font-semibold ${scoreColor(scanResult.overall)}`}>
                  {scoreLabel(scanResult.overall)}
                </p>
                <p className="mt-1 text-sm text-gray-400 text-center max-w-xs">
                  {scanResult.overall >= 75
                    ? 'Your resume is well-structured. See the breakdown for targeted improvements.'
                    : scanResult.overall >= 55
                    ? 'Your resume passes basic checks. See exactly what to fix to land more interviews.'
                    : 'Your resume may be filtered before a human sees it. See the specific issues below.'}
                </p>
              </div>

              {/* Category breakdown — blurred to drive signup */}
              <div className="space-y-4 mb-8">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Category breakdown
                </p>
                {(
                  Object.entries(CATEGORY_LABELS) as [
                    keyof typeof CATEGORY_LABELS,
                    string,
                  ][]
                ).map(([key, label], i) => (
                  <CategoryBar
                    key={key}
                    label={label}
                    score={scanResult[key].score}
                    blurred={i > 0} // first category visible, rest blurred
                  />
                ))}

                <div className="relative">
                  <div className="absolute inset-0 flex items-center justify-center z-10">
                    <div className="bg-white/90 backdrop-blur-sm border border-gray-200 rounded-xl px-4 py-3 text-center shadow-sm">
                      <p className="text-sm font-semibold text-gray-900 mb-0.5">Unlock full breakdown</p>
                      <p className="text-xs text-gray-500">Create a free account to see all issues and suggestions</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <Link
                to={`/signup?email=${encodeURIComponent(email)}`}
                className="block w-full py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-colors text-center"
              >
                See Full Breakdown — Free
              </Link>

              <p className="mt-3 text-xs text-center text-gray-400">
                No credit card required. Full score report with 1-click fixes.
              </p>
            </div>
          )}

        </div>
      </main>
    </div>
  );
}
